import {Injectable} from '@angular/core';
import {ApiService} from "../global/api.service";
import {Product, ProductNutritionScore, ProductReport, ProductReportInfo} from '../../types/models';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  constructor(
    private api: ApiService,
  ) {}

  public async get(Id: string): Promise<Product> {
    const product: Product = await this.api.get(`product/${Id}`);
    if (product) {
      product.ProductNutritionScores = this.getProductNutritionScoresSorted(product);
      product.TrafficLightProductNutritionScores = this.getTrafficLightProductNutritionScores(product);
      product.Favorite = product.Favorite || { ProductId: Id, IsUserFavorite: false};
    }
    return product;
  }

  getTrafficLightProductNutritionScores(product: Product): ProductNutritionScore[] {
    if (!product.ProductNutritionScores || product.ProductNutritionScores.length === 0) {
      return null;
    }
    const scores = [];
    for (const productNutritionScore of product.ProductNutritionScores) {
      if (productNutritionScore.NutritionScore.Traffic && productNutritionScore.NutritionScore.Low && productNutritionScore.NutritionScore.High) {
        scores.push(productNutritionScore);
      }
      for (const child of productNutritionScore.Children) {
        if (child.NutritionScore.Traffic && child.NutritionScore.Low && child.NutritionScore.High) {
          scores.push(child);
        }
      }
    }
    return scores;
  }

  getProductNutritionScoresSorted(product: Product): ProductNutritionScore[] {
    if (!product.ProductNutritionScores) {
      return [];
    }
    const scores = [];
    for (const productNutritionScore of product.ProductNutritionScores) {
      if (!productNutritionScore.NutritionScore) {
        continue;
      }
      const copy: ProductNutritionScore = JSON.parse(JSON.stringify(productNutritionScore));
      if (copy.NutritionScore.ParentId) {
        continue;
      }
      copy.Children = [];
      for (const nutritionScoreChild of copy.NutritionScore.Children) {
        const child = product.ProductNutritionScores.find(score => score.NutritionScore && score.NutritionScore.Id === nutritionScoreChild.Id);
        if (child) {
          copy.Children.push(child);
        }
      }
      scores.push(copy);
    }
    return scores;
  }

  public async getSuggestionsForProduct(productId: string, numberOfItems: number, loadedItems: number = 0): Promise<Product[]> {
    const params: any = {};
    if (loadedItems) {
      params.offset = loadedItems;
    }
    if (numberOfItems) {
      params.limit = numberOfItems;
    }
    return this.api.get(`product/suggestions/${productId}`, params);
  }

  private reportEnabled: { value: boolean, loadedAt: number } = null;

  // the store of the customer accepts reports of product errors, cached for five minutes
  public async isReportEnabled(): Promise<boolean> {
    if (!this.reportEnabled || Date.now() - this.reportEnabled.loadedAt > 5 * 60 * 1000) {
      const response = await this.api.get('product/report/enabled');
      this.reportEnabled = {value: !!response?.Enabled, loadedAt: Date.now()};
    }
    return this.reportEnabled.value;
  }

  // fields of the product for the report of an error and the reward, which the store promises now
  public async getReportInfo(productId: string): Promise<ProductReportInfo> {
    return this.api.get(`product/report/info/${productId}`);
  }

  public async createReport(productId: string, field: string, suggestedValue: string, comment: string): Promise<{ Id: string, Reward: number | null }> {
    return this.api.post('product/report', {ProductId: productId, Field: field, SuggestedValue: suggestedValue, Comment: comment});
  }

  public async getMyReports(): Promise<ProductReport[]> {
    return this.api.get('product/report/mine');
  }

  public async getSuggestions(): Promise<Product[]> {
    return this.api.get('product/suggestions');
  }

  public getPricePerBaseUnit(product: Product): number {
    const price = product.PriceGross;
    const baseAmount = this.getBaseAmount(product);
    if (!product.Amount || !baseAmount) {
      return 0;
    }
    if (product.Amount < baseAmount) {
      const percentage = product.Amount / baseAmount;
      return price / percentage;
    }

    return baseAmount * price / product.Amount;
  }

  public getBaseAmount(product: Product): number {
    if (!product || !product.Unit) {
      return 0;
    }
    return product.Unit === 'Stk' ? 1 : 100;
  }

  public getBaseUnit(product: Product): string {
    if (!product) {
      return '';
    }
    return product.MeanAmountUnit ? product.MeanAmountUnit : product.Unit
  }

  /**
   * Amount for display, pieces with the weight or volume per piece: "6 Stk à 250 g", withTotal (detail, delivery) also
   * of all pieces: "6 Stk à 250 g (gesamt 1,5 kg)"
   */
  public getAmountForDisplay(product: Product, withTotal = false): string {
    if (!product || !product.Unit || product.Amount === null || product.Amount === undefined) {
      return '';
    }
    const amount = ProductService.formatAmount(product.Amount, product.Unit);
    const mean = ProductService.getMeanAmounts(product);
    if (!mean) {
      return amount;
    }
    const piece = amount + ' à ' + ProductService.formatAmount(mean.Piece, product.MeanAmountUnit);
    return withTotal && Number(product.Amount) !== 1 ? piece + ' (gesamt ' + ProductService.formatAmount(mean.Total, product.MeanAmountUnit) + ')' : piece;
  }

  // mean weight or volume of one piece and of all pieces (MeanAmountType: of which MeanAmount is), null without
  public static getMeanAmounts(product: Product): { Piece: number, Total: number } {
    const pieces = Number(product?.Amount);
    if (product?.Unit !== 'Stk' || !(product.MeanAmount > 0) || !product.MeanAmountUnit || !(pieces > 0)) {
      return null;
    }
    const total = product.MeanAmountType === 'Total';
    return {Piece: total ? product.MeanAmount / pieces : product.MeanAmount, Total: total ? product.MeanAmount : product.MeanAmount * pieces};
  }

  // brand of the product (lists: Brand, detail: Info.Brand), it is not part of the name
  public getBrand(product: Product): string {
    return (product?.Brand || product?.Info?.Brand || '').trim();
  }

  // name for lists: delivery views of the api put brand, name and amount in NameShort (older apps), the name is in DisplayName
  public getDisplayName(product: Product): string {
    return product?.DisplayName || product?.NameShort || product?.Name || '';
  }

  // search list (and delivery views): brand and amount in one line, e.g. "Haribo · 175 g" or "Kiwi · 1 kg, Klasse I, Spanien"
  public getSlimText(product: Product, withTotal = false): string {
    const info = [this.getAmountForDisplay(product, withTotal), product?.Quality, product?.Origin].filter(Boolean).join(', ');
    return [this.getBrand(product), info].filter(Boolean).join(' · ');
  }

  /**
   * Amount in a readable unit: 1450 g -> "1,45 kg", 0,5 kg -> "500 g", 1500 ml -> "1,5 l", 33 cl -> "330 ml",
   * 2500 mg -> "2,5 g"; German number format, at most 2 decimals.
   */
  public static formatAmount(amount: number, unit: string): string {
    const value = Number(amount);
    if (!unit || !isFinite(value)) {
      return '';
    }
    const scales: { [unit: string]: [string, number] } = {
      mg: ['g', 0.001], g: ['g', 1], kg: ['g', 1000],
      ml: ['ml', 1], cl: ['ml', 10], dl: ['ml', 100], l: ['ml', 1000]
    };
    const scale = scales[unit.toLowerCase()];
    if (!scale || value === 0) {
      return ProductService.formatNumber(value) + ' ' + unit;
    }
    // base unit (g, ml), then the largest unit with a value of at least 1
    const base = value * scale[1];
    const steps: [string, number][] = scale[0] === 'g' ? [['kg', 1000], ['g', 1], ['mg', 0.001]] : [['l', 1000], ['ml', 1]];
    // drinks stay in litres as on the bottle ("0,75 l"), only below 0,1 l in ml
    const step = unit.toLowerCase() === 'l' && Math.abs(base) >= 100
      ? steps[0]
      : steps.find(([, factor]) => Math.abs(base) >= factor) || steps[steps.length - 1];
    return ProductService.formatNumber(base / step[1]) + ' ' + step[0];
  }

  private static formatNumber(value: number): string {
    return value.toLocaleString('de-DE', { maximumFractionDigits: 2 });
  }

  public getBaseAmountForNutrition(product: Product) {
    if (!product) {
      return '';
    }
      return '100 ' + this.getBaseUnit(product);
  }

  public getTrafficLightColor(score: ProductNutritionScore) {
    if (!score || !score.NutritionScore) {
      return null;
    }
    const value = score.Value;
    const low = score.NutritionScore.Low;
    const high = score.NutritionScore.High;
    if (low == null || high == null || value == null) {
      return null;
    }
    if (value < low) {
      return 'success';
    } else if (value < high) {
      return 'warning';
    }
    return 'danger';
  }

  public getProductInStock(product: Product): boolean {
    if (product?.Draft) {
      return false;
    } else if (product?.Stock) {
      return product.Stock.Available || (product.Stock.StockCount && product.Stock.StockCount >= product.Stock.StockLimit);
    }
    return false;
  }

  public sortProductsByAvailability(products: Product[]): Product[] {
    if (!products) {
      return [];
    }
    const availableProducts = [];
    const outOfStockProducts = [];
    for (const product of products) {
      if (this.getProductInStock(product)) {
        availableProducts.push(product);
      } else {
        outOfStockProducts.push(product);
      }
    }
    const sortedProducts = [];
    if (availableProducts.length > 0) {
      sortedProducts.push(...availableProducts);
    }
    if (outOfStockProducts.length > 0) {
      sortedProducts.push(...outOfStockProducts);
    }
    return sortedProducts;
  }

}
